import React, { useState } from 'react';
import './FallbackComponent.css';

export function FallbackComponent({
  error,
  errorInfo,
  retry,
  reset,
  retryCount = 0,
  maxRetries = 3,
  isRetrying = false,
  componentName = 'Application'
}) {
  const [showDetails, setShowDetails] = useState(false);
  const isNetworkError = error?.message?.toLowerCase().includes('network') ||
                        error?.message?.toLowerCase().includes('fetch');
  const isMaxRetriesReached = retryCount >= maxRetries;

  const handleRetry = async () => {
    if (retry) {
      await retry();
    }
  };

  const getErrorTitle = () => {
    if (isNetworkError) return 'Connection Problem';
    if (isMaxRetriesReached) return 'Unable to Load';
    return 'Oops! Something went wrong';
  };

  const getErrorMessage = () => {
    if (isNetworkError) return 'Please check your internet connection and try again.';
    if (isMaxRetriesReached) return "We've tried multiple times but couldn't load this content.";
    return 'We encountered an unexpected error. Please try again.';
  };

  // Don't show anything for initial load - just return a simple loading spinner
  if (!error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <div style={{ fontSize: '24px' }}>⏳</div>
      </div>
    );
  }

  return (
    <div className="fallback-container" role="alert" aria-live="assertive">
      <div className="fallback-content">
        <div className="fallback-icon">
          {isNetworkError ? '🌐' : '⏳'}
        </div>
        <h2 className="fallback-title">{getErrorTitle()}</h2>
        <p className="fallback-message">{getErrorMessage()}</p>
        {error?.message && (
          <p className="fallback-error-text">
            {isMaxRetriesReached ? 'Error: maximum retry attempts reached' : error.message}
          </p>
        )}
        {retryCount > 0 && !isMaxRetriesReached && (
          <p className="fallback-retry-count">
            Attempt {retryCount} of {maxRetries}
          </p>
        )}
        <div className="fallback-actions">
          {retry && !isMaxRetriesReached && (
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className="fallback-button fallback-button-primary"
              aria-label="Retry loading"
            >
              {isRetrying ? 'Retrying...' : 'Try Again'}
            </button>
          )}
          {reset && (
            <button
              onClick={reset}
              className="fallback-button fallback-button-secondary"
              aria-label="Reset application"
            >
              Reset
            </button>
          )}
          {isMaxRetriesReached && (
            <button
              onClick={() => window.location.reload()}
              className="fallback-button fallback-button-primary"
              aria-label="Refresh page"
            >
              Refresh Page
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default FallbackComponent;